import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpTrayIcon, BuildingOfficeIcon, CpuChipIcon } from '@heroicons/react/24/outline';
import { PageBackButton } from '@/components/common/PageBackButton';
import { ROUTES } from '@/constants/routes';
import api from '@/services/api/client';
import { useTranslation } from '@/store/languageStore';
import toast from 'react-hot-toast';

type Status = 'all' | 'stock' | 'assigned';

interface InventoryRow {
  id: number;
  productCode: string;
  boxNo: string | null;
  serialNumber: string | null;
  macAddress: string;
  deviceName: string;
  firmwareVersion: string | null;
  organizationId: number | null;
  organizationName: string | null;
  trackerLabel: string | null;
}

interface InventoryPage {
  total: number;
  stock: number;
  assigned: number;
  page: number;
  pageSize: number;
  rows: InventoryRow[];
}

interface TopNode {
  id: number;
  name: string;
}

interface Issue {
  excelRow?: number;
  id?: number;
  code: string;
}

function issueText(t: (key: string) => string, issue: Issue): string {
  const label = t(`eyeInventory.errors.${issue.code}`);
  const shown = label === `eyeInventory.errors.${issue.code}` ? issue.code : label;
  if (issue.excelRow) return t('eyeInventory.rowIssue').replace('{row}', String(issue.excelRow)).replace('{message}', shown);
  return shown;
}

export default function EyeInventoryPage() {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<Status>('all');
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [page, setPage] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);
  const [data, setData] = useState<InventoryPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<number[]>([]);
  const [customers, setCustomers] = useState<TopNode[]>([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [orgFilter, setOrgFilter] = useState('');
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<{ success: boolean; data: InventoryPage }>('/api/admin/eye-inventory', {
        params: { status, q: submitted, page },
      })
      .then((response) => {
        if (!cancelled) setData(response.data.data);
      })
      .catch(() => {
        if (!cancelled) toast.error(t('eyeInventory.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, submitted, page, reloadToken, t]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const visibleIds = data?.rows.map((row) => row.id) ?? [];
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
  const filteredCustomers = useMemo(
    () => customers.filter((org) => !orgFilter.trim() || org.name.toLowerCase().includes(orgFilter.toLowerCase())),
    [customers, orgFilter],
  );

  const reload = () => {
    setSelected([]);
    setPage(1);
    setReloadToken((current) => current + 1);
  };

  const openAssign = async () => {
    setOrganizationId(null);
    setOrgFilter('');
    setAssignOpen(true);
    if (customers.length > 0) return;
    try {
      const response = await api.get<{ success: boolean; data: TopNode[] }>('/api/admin/eye-inventory/customers');
      setCustomers(response.data.data);
    } catch {
      toast.error(t('eyeInventory.loadError'));
    }
  };

  const importFile = async (file: File) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await api.post<{
        success: boolean;
        data: { created: number; updated: number; reconciled: number; issues: Issue[] };
      }>('/api/admin/eye-inventory/import', form);
      const result = response.data.data;
      toast.success(
        t('eyeInventory.importDone')
          .replace('{created}', String(result.created))
          .replace('{updated}', String(result.updated)),
      );
      result.issues.slice(0, 5).forEach((issue) => toast.error(issueText(t, issue)));
      reload();
    } catch (error: unknown) {
      const code = (error as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code ? issueText(t, { code }) : t('eyeInventory.importError'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const assign = async () => {
    if (!organizationId) return;
    setBusy(true);
    try {
      const response = await api.post<{ success: boolean; data: { assigned: number; errors: Issue[] } }>(
        '/api/admin/eye-inventory/assign',
        { ids: selected, organizationId },
      );
      toast.success(t('eyeInventory.assigned').replace('{count}', String(response.data.data.assigned)));
      response.data.data.errors.slice(0, 5).forEach((issue) => toast.error(issueText(t, issue)));
      setAssignOpen(false);
      setSelected([]);
      reload();
    } catch (error: unknown) {
      const code = (error as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code ? issueText(t, { code }) : t('eyeInventory.assignError'));
    } finally {
      setBusy(false);
    }
  };

  const unassign = async (ids: number[]) => {
    setBusy(true);
    try {
      const response = await api.post<{ success: boolean; data: { unassigned: number; errors: Issue[] } }>(
        '/api/admin/eye-inventory/unassign',
        { ids },
      );
      toast.success(t('eyeInventory.unassigned').replace('{count}', String(response.data.data.unassigned)));
      response.data.data.errors.slice(0, 5).forEach((issue) => toast.error(issueText(t, issue)));
      setSelected([]);
      reload();
    } catch {
      toast.error(t('eyeInventory.assignError'));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: number) => {
    setSelected((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  return (
    <div className="min-h-screen bg-bgLight">
      <div className="mx-auto max-w-7xl space-y-4 px-4 py-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <PageBackButton fallback={ROUTES.SYSTEM_ADMINISTRATION} />
            <div>
              <h1 className="flex items-center gap-2 text-2xl font-bold text-textDefault">
                <CpuChipIcon className="h-6 w-6 text-primary" />
                {t('eyeInventory.title')}
              </h1>
              <p className="text-sm text-textSecondary">{t('eyeInventory.description')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void importFile(file);
              }}
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40"
            >
              <ArrowUpTrayIcon className="h-4 w-4" />
              {t('eyeInventory.import')}
            </button>
            <button
              type="button"
              disabled={busy || selected.length === 0}
              onClick={() => void openAssign()}
              className="rounded-lg border border-border px-4 py-2 text-sm text-textDefault hover:bg-bgElevated disabled:opacity-40"
            >
              {t('eyeInventory.assign')} ({selected.length})
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {([
            ['all', t('eyeInventory.all'), (data?.stock ?? 0) + (data?.assigned ?? 0)],
            ['stock', t('eyeInventory.stock'), data?.stock ?? 0],
            ['assigned', t('eyeInventory.assignedFilter'), data?.assigned ?? 0],
          ] as const).map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setStatus(key);
                setPage(1);
              }}
              className={`rounded-xl border p-4 text-left ${status === key ? 'border-blue-500 bg-bgElevated' : 'border-border bg-bgElevated'}`}
            >
              <p className="text-sm text-textSecondary">{label}</p>
              <p className="text-2xl font-bold text-textDefault">{loading && !data ? '…' : count}</p>
            </button>
          ))}
        </div>

        <form
          className="flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setSubmitted(query.trim());
          }}
        >
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('eyeInventory.search')}
            className="w-full max-w-md rounded-md border border-border bg-bgElevated px-3 py-2 text-sm text-textDefault"
          />
          <button type="submit" className="rounded-md border border-border px-3 py-2 text-sm text-textDefault">
            {t('common.search')}
          </button>
        </form>

        <div className="overflow-x-auto rounded-xl border border-border bg-bgElevated">
          <table className="min-w-full text-sm">
            <thead className="text-left text-textSecondary">
              <tr>
                <th className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={() => setSelected(allVisibleSelected ? [] : visibleIds)}
                    aria-label={t('eyeInventory.selectAll')}
                  />
                </th>
                <th className="px-3 py-2">{t('eyeInventory.name')}</th>
                <th className="px-3 py-2">MAC</th>
                <th className="px-3 py-2">{t('eyeInventory.serial')}</th>
                <th className="px-3 py-2">{t('eyeInventory.box')}</th>
                <th className="px-3 py-2">{t('eyeInventory.firmware')}</th>
                <th className="px-3 py-2">{t('eyeInventory.customer')}</th>
                <th className="px-3 py-2">{t('eyeInventory.tracker')}</th>
              </tr>
            </thead>
            <tbody>
              {data?.rows.map((row) => (
                <tr key={row.id} className="border-t border-border">
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={selected.includes(row.id)} onChange={() => toggle(row.id)} />
                  </td>
                  <td className="px-3 py-2 text-textDefault">
                    <div>{row.deviceName}</div>
                    <div className="text-xs text-textSecondary">{row.productCode}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-textDefault">{row.macAddress}</td>
                  <td className="px-3 py-2 text-textDefault">{row.serialNumber || '—'}</td>
                  <td className="px-3 py-2 text-textDefault">{row.boxNo || '—'}</td>
                  <td className="px-3 py-2 text-textDefault">{row.firmwareVersion || '—'}</td>
                  <td className="px-3 py-2">
                    {row.organizationName ? (
                      <span className="inline-flex items-center gap-1 text-textDefault">
                        <BuildingOfficeIcon className="h-4 w-4" />
                        {row.organizationName}
                      </span>
                    ) : (
                      <button type="button" className="text-orange-600" onClick={() => { setSelected([row.id]); void openAssign(); }}>
                        {t('eyeInventory.inStock')}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2 text-textDefault">
                    {row.trackerLabel || '—'}
                    {row.organizationId && !row.trackerLabel && (
                      <button type="button" className="ml-2 text-xs text-textSecondary underline" onClick={() => void unassign([row.id])}>
                        {t('eyeInventory.unassign')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && data?.rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-textSecondary">{t('eyeInventory.empty')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between text-sm text-textSecondary">
          <span>{t('eyeInventory.page').replace('{page}', String(page)).replace('{pages}', String(totalPages)).replace('{total}', String(data?.total ?? 0))}</span>
          <div className="flex gap-2">
            <button type="button" disabled={page <= 1} className="rounded border border-border px-3 py-1 disabled:opacity-40" onClick={() => setPage((current) => current - 1)}>{t('eyeInventory.prev')}</button>
            <button type="button" disabled={page >= totalPages} className="rounded border border-border px-3 py-1 disabled:opacity-40" onClick={() => setPage((current) => current + 1)}>{t('eyeInventory.next')}</button>
          </div>
        </div>
      </div>

      {assignOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-xl bg-bgContainer p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-textDefault">{t('eyeInventory.assignTitle')}</h2>
            <p className="mb-3 text-sm text-textSecondary">{t('eyeInventory.assignHint').replace('{count}', String(selected.length))}</p>
            <input
              value={orgFilter}
              onChange={(event) => setOrgFilter(event.target.value)}
              placeholder={t('eyeInventory.searchCustomer')}
              className="mb-2 w-full rounded-md border border-border bg-bgLight px-3 py-2 text-sm text-textDefault"
            />
            <div className="max-h-52 overflow-y-auto rounded-md border border-border">
              {filteredCustomers.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => setOrganizationId(org.id)}
                  className={`block w-full px-3 py-2 text-left text-sm ${organizationId === org.id ? 'bg-blue-600 text-white' : 'text-textDefault hover:bg-bgLight'}`}
                >
                  {org.name}
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="rounded-lg border border-border px-4 py-2 text-sm" onClick={() => setAssignOpen(false)}>{t('common.cancel')}</button>
              <button type="button" disabled={!organizationId || busy} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white disabled:opacity-40" onClick={() => void assign()}>{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
